import {
  FamilyApplicationAPIs,
  FamilyApplicationEndpoints,
  FamilyApplicationResp,
  FamilyApplicationRespSchema,
  FamilyAttestation,
  GETFamilyApplication,
  GetTaxCommissionIncomeDetails,
  GetTaxCommissionIncomeDetailsReq,
  GetTaxCommissionIncomeDetailsResp,
  HouseHoldIncomeTypeSchema,
  IncomeEvaluation,
  NonTaxableIncome,
  POSTApplicantDetails,
  PostTaxComissionUserConfirmation,
  PostTaxComissionUserConfirmationReq,
  PostTaxComissionUserConfirmationResp,
  PUTFamilyAttestation,
  PUTIncomeDetails,
  PUTSubmitApplication,
  SSNOnly,
  Student,
  UpdateAcctAttestationReq,
  UpdateAcctAttestationResp,
  UpdateApplicantResp,
  UpdateApplicationReq,
  UpdateIncomeDetailsReq,
  UpdateIncomeDetailsResp,
} from '@/apis/family_application/requests.ts'
import { RootState } from '@/app-storage/store.ts'
import {
  REQUEST_STATUS,
  ThunkRequestState,
} from '@/app/common/types_and_interfaces'
import { TaxForms } from '@/app/pages/family_application/income_verification/IncomeVerificationWizardProvider'
import { PostBCResponse } from '@/app/pages/family_application/student_details/hooks/usePostBirthCertificate.tsx'
import {
  RelationshipForms,
  SpecialEducationForms,
} from '@/app/pages/family_application/student_details/hooks/useStudentDetailsDocumentParams'
import { INITIAL_ERROR } from '@/common/constants.ts'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createAppAsyncThunk } from './familyApplication.constants'
import { updateStudent } from './upsert-student.action'

export type FamilyApplicationState = {
  request: ThunkRequestState
  data: FamilyApplicationResp | null
  selectedStudentId: number | null
  ocr_bc_attempt_number: number
  tax_commission_response: GetTaxCommissionIncomeDetailsResp 
}

export const InitialRequestState: ThunkRequestState = {
  status: REQUEST_STATUS.IDLE,
  isFirstFetch: true,
  error: INITIAL_ERROR,
} as const

export const InitialTaxCommissionResponseState: GetTaxCommissionIncomeDetailsResp =
  [
    {
      FedAdjustedGrossIncome: 0,
      IncomeTaxYear: 0,
      NumberofDependents: 0,
      Filing: '',
      ResidentStatus: '',
      activity_log_id: 0,
      errors: [{
        code: 0,
        message: '',
      }],
    },
  ] as const

export const initialState: FamilyApplicationState = {
  request: { ...InitialRequestState },
  selectedStudentId: null,
  data: null,
  ocr_bc_attempt_number: 0,
  tax_commission_response: InitialTaxCommissionResponseState,
} as const

export const RefreshFamilyApplication = createAppAsyncThunk<
  FamilyApplicationResp,
  number
>(
  FamilyApplicationAPIs.getUserApplication,
  async function (programID, thunkAPI) {
    const response = await GETFamilyApplication(programID)
    if (response.error) {
      return thunkAPI.rejectWithValue(response.error)
    }
    return response.data
  }
)

function isSSNUpdate(data: unknown): data is SSNOnly {
  const hasSSN = (data as SSNOnly).SSN
  const hasSpouseSSN = (data as SSNOnly).spouse_ssn


  return !!hasSSN || !!hasSpouseSSN
}

export const UpdateApplicant = createAppAsyncThunk<
  UpdateApplicantResp,
  UpdateApplicationReq
>(
  FamilyApplicationEndpoints.update_family_applicant,
  async function (payload, thunkAPI) {
    const response = await POSTApplicantDetails(payload)
    if (response.error) {
      return thunkAPI.rejectWithValue(response.error)
    }





    if(isSSNUpdate(payload)) {
      thunkAPI.dispatch(
        setApplicantDetails({
          ...payload,
        })  
      )
    } else { 
      
      thunkAPI.dispatch(
        setApplicantDetails({
          ...payload,
        })  
      )
    }
       
    


    return response.data
  }
)

export const UpdateIncomeDetails = createAppAsyncThunk<
  UpdateIncomeDetailsResp,
  UpdateIncomeDetailsReq
>(
  FamilyApplicationEndpoints.update_family_income,
  async function (payload, thunkAPI) {
    const response = await PUTIncomeDetails(payload)
    if (response.error) {
      return thunkAPI.rejectWithValue(response.error)
    }

    return response.data
  }
)

export const TaxCommissionIncomeDetails = createAppAsyncThunk<
  GetTaxCommissionIncomeDetailsResp,
  GetTaxCommissionIncomeDetailsReq
>(
  FamilyApplicationEndpoints.get_family_income_tax_commission,
  async function (payload, thunkAPI) {
    const response = await GetTaxCommissionIncomeDetails(payload)
    if (response.error) {
      return thunkAPI.rejectWithValue(response.error)
    }

    thunkAPI.dispatch(setTaxCommissionResponse(response.data))

    return response.data
  }
)

export const SubmitTaxComissionUserConfirmation = createAppAsyncThunk<
  PostTaxComissionUserConfirmationResp,
  PostTaxComissionUserConfirmationReq
>(
  FamilyApplicationEndpoints.post_tax_commission_user_confirmation,
  async function (payload, thunkAPI) {
    const response = await PostTaxComissionUserConfirmation(payload)
    if (response.error) {
      return thunkAPI.rejectWithValue(response.error)
    }
    return response.data
  }
)

export const UpdateFamilyAttestation = createAppAsyncThunk<
  UpdateAcctAttestationResp,
  UpdateAcctAttestationReq
>(
  FamilyApplicationEndpoints.update_family_attestation,
  async function (payload, thunkAPI) {
    const response = await PUTFamilyAttestation(payload)
    if (response.error) {
      return thunkAPI.rejectWithValue(response.error)
    }
    return response.data
  }
)
type SubmitFamilyApplicationPayload = {
  application_id: number
  selected_student_ids: number[]
}
export const SubmitFamilyApplication = createAppAsyncThunk<
  //fix later
  any,
  SubmitFamilyApplicationPayload
>(
  FamilyApplicationEndpoints.submit_family_application,
  async function (payload, thunkAPI) {
    const response = await PUTSubmitApplication(
      payload.application_id,
      payload.selected_student_ids
    )

    if (response.error) {
      return thunkAPI.rejectWithValue(response.error)
    }
    return FamilyApplicationRespSchema.parse(response.data)
  }
)

type ApplicantDetails = Pick<
  FamilyApplicationResp,
  | 'first_name'
  | 'last_name'
  | 'middle_name'
  | 'address'
  | 'city'
  | 'zip_code'
  | 'global_state_id'
  | 'date_of_birth'
>


const familyApplicationSlice = createSlice({
  name: 'familyApplication',
  initialState,
  reducers: {
    setApplicationDocument: function (
      state,
      action: PayloadAction<{
        form:
          | NonTaxableIncome
          | TaxForms
          | SpecialEducationForms
          | RelationshipForms
        member: 'user' | 'partner'
        src: string
        submitted?: boolean
      }>
    ) {
      const { form, member, src, submitted = false } = action.payload
      if (state.data && state.data.documents) {
        const existingIndex = state.data.documents.findIndex(
          (doc) => doc.document_type === form && doc.documentMember === member
        )
        const newDocument = {
          document_type: form,
          documentMember: member,
          src,
          submitted,
        }
        state.data.documents =
          existingIndex === -1
            ? [...state.data.documents, newDocument]
            : state.data.documents.map((doc, index) => {
                if (index === existingIndex) {
                  return newDocument
                }
                return doc
              })
      }
    },
    setApplicationDocumentSubmitted: function (
      state,
      action: PayloadAction<{
        form: string
        member: string
      }>
    ) {
      const { form, member } = action.payload
      if (state.data && state.data.documents) {
        const existingIndex = state.data.documents.findIndex(
          (doc) => doc.document_type === form && doc.documentMember === member
        )
        if (existingIndex > -1) {
          state.data.documents = state.data.documents.map((doc, index) => {
            if (index === existingIndex) {
              return {
                document_type: doc.document_type,
                documentMember: doc.documentMember,
                src: doc.src,
                submitted: true,
              }
            }
            return doc
          })
        }
      }
    },
    setIdentityInfo: (state, action: PayloadAction<FamilyApplicationResp>) => {
      if (state.data) {
        state.data.first_name =
          action.payload.first_name ?? state.data.first_name
        state.data.last_name = action.payload.last_name ?? state.data.last_name
        state.data.zip_code = action.payload.zip_code ?? state.data.zip_code
        state.data.date_of_birth =
          action.payload.date_of_birth ?? state.data.date_of_birth
        state.data.phone = action.payload.phone ?? state.data.phone
        state.data.address = action.payload.address ?? state.data.address
        state.data.address2 = action.payload.address2 ?? state.data.address2
        state.data.city = action.payload.city ?? state.data.city
        state.data.county = action.payload.county ?? state.data.county
        state.data.global_state_id =
          action.payload.global_state_id ?? state.data.global_state_id
        state.data.is_military_exemption =
          action.payload.is_military_exemption ??
          state.data.is_military_exemption
      }
    },
    setResidencyDocument: (state, action: PayloadAction<{ src: string }>) => {
      if (state.data) {
        state.data.residencyDocument = action.payload.src
      }
    },
    setApplicantDetails: function (
      state,
      action: PayloadAction<Partial<ApplicantDetails> | Partial<SSNOnly>>
    ) {
      const cleanPayload = Object.fromEntries(
        Object.entries(action.payload).filter((entry) => entry[1] !== undefined)
      );

      if (state.data) {
      state.data = {...state.data, ...cleanPayload}
      }
    },
    upsertStudent: function (state, action: PayloadAction<Student>) {
      if (state.data?.students) {
        const { id: studentId } = action.payload
        const studentExistsLocally = state.data.students?.some(
          (student) => student.id === studentId
        )

        // Update Use Case
        if (studentExistsLocally) {
          state.selectedStudentId = studentId
          state.data.students = state.data.students.map((student) => {
            if (student.id === action.payload.id) {
              return action.payload
            }
            return student
          })
        }
        // Insert Use Case
        else {
          state.selectedStudentId = studentId
          state.data.students = [...state.data.students, action.payload]
        }
      }
    },
    setSelectedStudent: function (state, action: PayloadAction<number | null>) {
      state.selectedStudentId = action.payload
    },
    configureSliceForInsertStudent: function (state) {
      state.selectedStudentId = null
    },
    resetOCRAttempts: (state) => {
      state.ocr_bc_attempt_number = 0
    },
    setOCRAttempt: (state, action: PayloadAction<number>) => {
      state.ocr_bc_attempt_number = action.payload
    },
    setSelectedStudentDOB: function (state, action: PayloadAction<string>) {
      if (state.data?.students) {
        state.data.students = state.data.students.map((student) => {
          if (student.id === state.selectedStudentId) {
            return {
              ...student,
              date_of_birth: action.payload,
            }
          }
          return student
        })
      }
    },
    setIncomeEvaluatedBy: function (
      state,
      action: PayloadAction<IncomeEvaluation>
    ) {
      if (state.data) {
        state.data.income_evaluated_by = action.payload
      }
    },
    updateStudentBCInformation: function (
      state,
      action: PayloadAction<PostBCResponse>
    ) {
      if (state.data?.students) {
        state.data.students = state.data.students.map((student) => {
          if (student.id === state.selectedStudentId) {
            return {
              ...student,
              date_of_birth: action.payload.date_of_birth,
              birth_certificate_document_id: action.payload.student_document_id,
            }
          }
          return student
        })
      }
    },
    setTaxCommissionResponse: function (
      state,
      action: PayloadAction<GetTaxCommissionIncomeDetailsResp>
    ) {
      state.tax_commission_response = action.payload
    },
    addAttestations: function (
      state,
      action: PayloadAction<FamilyAttestation[]>
    ) {
      if (state.data) {
        const existingIds = state.data.attestations.map(
          (attestation) => attestation.id
        )
        const newAttestations = action.payload.filter(
          (incomingAttestation) => !existingIds.includes(incomingAttestation.id)
        )
        state.data.attestations = [
          ...state.data.attestations,
          ...newAttestations,
        ]
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(RefreshFamilyApplication.pending, (state) => {
        state.request.status = 'in_flight'
        state.request.error = INITIAL_ERROR
      })
      .addCase(RefreshFamilyApplication.fulfilled, (state, action) => {
        state.data = action.payload
        state.request.status = 'success'
        state.request.error = INITIAL_ERROR
        // we use this to track if a query has loaded before. There are scenarios where we don't want to trigger errors if we have loaded a query more than once.
        // We only need this property when a request is fulfilled, so I left it out for other events.
        state.request.isFirstFetch = false
      })
      .addCase(RefreshFamilyApplication.rejected, (state, action) => {
        state.request.status = 'failed'
        // API error occurred
        if (action.payload) {
          state.request.error = action.payload
        }
        // Library error occurred
        else {
          state.request.error.message = action.error.message ?? ''
        }
      })
      .addCase(UpdateApplicant.pending, function (state) {
        state.request.status = 'in_flight'
        state.request.error = INITIAL_ERROR
      })
      .addCase(UpdateApplicant.fulfilled, (state) => {
        state.request.status = 'success'
        state.request.error = INITIAL_ERROR
      })
      .addCase(UpdateApplicant.rejected, (state, action) => {
        state.request.status = 'failed'
        // API error occurred
        if (action.payload) {
          state.request.error = action.payload
        }
        // Library error occurred
        else {
          state.request.error.message = action.error.message ?? ''
        }
      })
      .addCase(updateStudent.pending, function (state) {
        state.request.status = 'in_flight'
        state.request.error = INITIAL_ERROR
      })
      .addCase(updateStudent.fulfilled, (state) => {
        state.request.status = 'success'
        state.request.error = INITIAL_ERROR
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.request.status = 'failed'
        // API error occurred
        if (action.payload) {
          state.request.error = action.payload
        }
        // Library error occurred
        else {
          state.request.error.message = action.error.message ?? ''
        }
      })
      .addCase(UpdateIncomeDetails.pending, function (state) {
        state.request.status = 'in_flight'
        state.request.error = INITIAL_ERROR
      })
      .addCase(UpdateIncomeDetails.fulfilled, (state, action) => {
        if (state.data) {
          const incomeTypeResult = HouseHoldIncomeTypeSchema.safeParse(
            action.payload.household_income_type
          )


          state.data.skip_income_verification = action.payload.skip_income_verification
          state.data.phone = action.payload.phone
          state.data.address = action.payload.address
          state.data.city = action.payload.city
          state.data.zip_code = action.payload.zip_code
          state.data.global_state_id = action.payload.global_state_id
          state.data.household_inhabitants =
            action.payload.household_inhabitants
          state.data.household_income_greater =
            action.payload.household_income_greater
          state.data.household_income_type = incomeTypeResult.success
            ? incomeTypeResult.data
            : null
          state.data.non_taxable_income = action.payload.non_taxable_income
          state.data.household_filed_federal_income =
            action.payload.household_filed_federal_income
        }
        state.request.status = 'success'
        state.request.error = INITIAL_ERROR
      })
      .addCase(UpdateIncomeDetails.rejected, (state, action) => {
        state.request.status = 'failed'
        // API error occurred
        if (action.payload) {
          state.request.error = action.payload
        }
        // Library error occurred
        else {
          state.request.error.message = action.error.message ?? ''
        }
      })
      .addCase(UpdateFamilyAttestation.pending, function (state) {
        state.request.status = 'in_flight'
        state.request.error = INITIAL_ERROR
      })
      .addCase(UpdateFamilyAttestation.fulfilled, (state, action) => {
        if (state.data) {
          const { id: targetID, status } = action.payload
          state.data.attestations = state.data.attestations.map(
            (attestation) => {
              if (attestation.id === targetID) {
                return { ...attestation, status }
              }
              return attestation
            }
          )
        }
        state.request.status = 'success'
        state.request.error = INITIAL_ERROR
      })
      .addCase(UpdateFamilyAttestation.rejected, (state, action) => {
        state.request.status = 'failed'
        // API error occurred
        if (action.payload) {
          state.request.error = action.payload
        }
        // Library error occurred
        else {
          state.request.error.message = action.error.message ?? ''
        }
      })
      .addCase(SubmitFamilyApplication.pending, function (state) {
        state.request.status = 'in_flight'
        state.request.error = INITIAL_ERROR
      })
      .addCase(SubmitFamilyApplication.fulfilled, (state, action) => {
        state.data = action.payload
        state.request.status = 'success'
        state.request.error = INITIAL_ERROR
      })
      .addCase(SubmitFamilyApplication.rejected, (state, action) => {
        state.request.status = 'failed'
        if (action.payload) {
          state.request.error = action.payload
        }
      })
      .addCase(SubmitTaxComissionUserConfirmation.pending, function (state) {
        state.request.status = 'in_flight'
        state.request.error = INITIAL_ERROR
      })
      .addCase(SubmitTaxComissionUserConfirmation.fulfilled, (state) => {
        state.request.status = 'success'
        state.request.error = INITIAL_ERROR
      })
      .addCase(SubmitTaxComissionUserConfirmation.rejected, (state, action) => {
        state.request.status = 'failed'
        // API error occurred
        if (action.payload) {
          state.request.error = action.payload
        }
        // Library error occurred
        else {
          state.request.error.message = action.error.message ?? ''
        }
      })
    //Total alternative docs
  },
})

export const {
  setApplicantDetails,
  setIdentityInfo,
  upsertStudent,
  setSelectedStudent,
  setIncomeEvaluatedBy,
  configureSliceForInsertStudent,
  setApplicationDocument,
  setApplicationDocumentSubmitted,
  setResidencyDocument,
  resetOCRAttempts,
  setOCRAttempt,
  setSelectedStudentDOB,
  setTaxCommissionResponse,
  addAttestations,
  updateStudentBCInformation,
} = familyApplicationSlice.actions

export default familyApplicationSlice.reducer

// Selectors
export const selectApplicantDetails = createSelector(
  (state: RootState) => state.FamilyApplication.data,
  function (applicationData) {
    return {
      first_name: applicationData?.first_name ?? '',
      last_name: applicationData?.last_name ?? '',
      phone: applicationData?.phone ?? '',
      address: applicationData?.address ?? '',
      city: applicationData?.city ?? '',
      zip_code: applicationData?.zip_code ?? '',
      global_state_id: applicationData?.global_state_id ?? null,
    }
  }
)

// the selectSelectedStudent function is used throughout the application.
// I need a version where student is guaranteed to be defined in the application.
// If it is not, it should throw an error.
// Only use this in areas where you know the student will be defined.
export const unsafe_selectSelectedStudent = (state: RootState) => {
  if (state.FamilyApplication.data == null) {
    throw new Error('Application data is null')
  }
  const student = state.FamilyApplication.data.students?.find(
    (student) => student.id == state.FamilyApplication.selectedStudentId
  )
  if (!student) {
    throw new Error('Selected student is null')
  }

  return student
}

export const selectSelectedStudent = function (state: RootState) {
  if (state.FamilyApplication.data == null) {
    throw new Error('Application data is null')
  }
  return state.FamilyApplication.data.students?.find(
    (student) => student.id == state.FamilyApplication.selectedStudentId
  )
}

export const selectApplicationRequestStatus = (state: RootState) =>
  state.FamilyApplication.request.status

export const selectFamilyApplication = (state: RootState) => {
  if (state.FamilyApplication.data == null) {
    throw new Error('Application data is null')
  }
  return state.FamilyApplication.data
}
